import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { driveApi, type DriveItem } from "./driveApi";
import { useAccessToken } from "@/auth/useAccessToken";

// ──────────────────────────────────────────────────────────────────
// Query keys. Everything lives under ["drive", <kind>, ...] so a single
// invalidate of ["drive"] refreshes lists, folders, quota, ancestors…
// ──────────────────────────────────────────────────────────────────

const keys = {
    items: (parentId: string | null, token: string | undefined) => ["drive", "items", parentId, token] as const,
    item: (id: string, token: string | undefined) => ["drive", "item", id, token] as const
};

/** Refresh every Drive query (lists, folders, quota, ancestors, search…). */
export function invalidateDrive(qc: QueryClient): Promise<void> {
    return qc.invalidateQueries({ queryKey: ["drive"] });
}

// ── Queries ─────────────────────────────────────────────────────────

export function useItems(parentId: string | null, opts?: { enabled?: boolean }) {
    const token = useAccessToken();
    return useQuery({
        queryKey: keys.items(parentId, token),
        queryFn: () => driveApi.listItems(parentId, token),
        enabled: Boolean(token) && (opts?.enabled ?? true),
        refetchOnWindowFocus: true
    });
}

export function useItem(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: id ? keys.item(id, token) : ["drive", "item", "none"],
        queryFn: () => (id ? driveApi.getItem(id, token) : Promise.reject(new Error("no id"))),
        enabled: Boolean(id) && Boolean(token),
        retry: false
    });
}

export function useAncestors(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "ancestors", id, token],
        queryFn: () => (id ? driveApi.ancestors(id, token) : Promise.resolve([])),
        enabled: Boolean(id) && Boolean(token)
    });
}

export function useTrash() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "trash", token],
        queryFn: () => driveApi.listTrash(token),
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });
}

export function useFavorites() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "favorites", token],
        queryFn: () => driveApi.listFavorites(token),
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });
}

export function useRecent(take = 50) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "recent", take, token],
        queryFn: () => driveApi.listRecent(take, token),
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });
}

/**
 * Drive full-text search. Empty query returns nothing without hitting the
 * network so the search box can debounce without a loading flash.
 */
export function useSearch(q: string, take = 25) {
    const token = useAccessToken();
    const trimmed = q.trim();
    return useQuery({
        queryKey: ["drive", "search", trimmed, take, token],
        queryFn: () => driveApi.search(trimmed, token, take),
        enabled: Boolean(token) && trimmed.length > 0,
        staleTime: 10_000
    });
}

/** The template gallery's catalogue (changes only on a deploy). */
export function useTemplates() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "templates", token],
        queryFn: () => driveApi.templates(token),
        enabled: Boolean(token),
        staleTime: 10 * 60_000
    });
}

export function useQuota() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "quota", token],
        queryFn: () => driveApi.quota(token),
        enabled: Boolean(token),
        staleTime: 30_000
    });
}

/** Storage page: usage by type, old versions, bin. */
export function useStorageSummary() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "storage", "summary", token],
        queryFn: () => driveApi.storageSummary(token),
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });
}

const LARGEST_PAGE = 25;

/** Storage page: the caller's files by size, paged ("Show more"). */
export function useLargestFiles(order: "asc" | "desc") {
    const token = useAccessToken();
    return useInfiniteQuery({
        queryKey: ["drive", "storage", "largest", order, token],
        queryFn: ({ pageParam }) => driveApi.largestFiles(pageParam, LARGEST_PAGE, order, token),
        initialPageParam: 0,
        getNextPageParam: (last, all) => {
            const loaded = all.reduce((n, p) => n + p.items.length, 0);
            return last.items.length > 0 && loaded < last.total ? loaded : undefined;
        },
        enabled: Boolean(token)
    });
}

/** One item's timeline (a folder includes its direct children). */
export function useItemActivity(itemId: string | null) {
    const token = useAccessToken();
    return useInfiniteQuery({
        queryKey: ["drive", "activity", "item", itemId, token],
        queryFn: ({ pageParam }) =>
            itemId ? driveApi.itemActivity(itemId, token, 30, pageParam) : Promise.reject(new Error("no id")),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => last.nextBefore ?? null,
        enabled: Boolean(itemId) && Boolean(token),
        refetchOnWindowFocus: true
    });
}

/** The caller's activity feed (own items + shared with them), newest first. */
export function useActivityFeed() {
    const token = useAccessToken();
    return useInfiniteQuery({
        queryKey: ["drive", "activity", "feed", token],
        queryFn: ({ pageParam }) => driveApi.activityFeed(token, 40, pageParam),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => last.nextBefore ?? null,
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });
}

/** Flat list of every folder the caller owns (move picker). */
export function useAllFolders() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "folders", "all", token],
        queryFn: () => driveApi.listAllFolders(token),
        enabled: Boolean(token),
        staleTime: 30_000
    });
}

export function useVersions(itemId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "versions", itemId, token],
        queryFn: () => (itemId ? driveApi.listVersions(itemId, token) : Promise.reject(new Error("no id"))),
        enabled: Boolean(itemId) && Boolean(token)
    });
}

export function useShares(itemId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "shares", itemId, token],
        queryFn: () => (itemId ? driveApi.listShares(itemId, token) : Promise.reject(new Error("no id"))),
        enabled: Boolean(itemId) && Boolean(token)
    });
}

// ── Optimistic-update plumbing ──────────────────────────────────────

const LIST_KINDS = new Set(["items", "favorites", "recent", "search", "trash"]);
const isListKey = (key: QueryKey) => key[0] === "drive" && LIST_KINDS.has(String(key[1]));
const isItemKey = (key: QueryKey) => key[0] === "drive" && key[1] === "item";

type Snapshot = Array<[QueryKey, unknown]>;

/**
 * Cancels in-flight list fetches, snapshots every cached item list (and
 * single-item query) and applies `patch` to each so the UI changes
 * instantly. Return value feeds `rollback` on error.
 */
async function patchCaches(
    qc: QueryClient,
    patchList: (kind: string, key: QueryKey, list: DriveItem[]) => DriveItem[],
    patchItem?: (item: DriveItem) => DriveItem
): Promise<Snapshot> {
    await qc.cancelQueries({ predicate: (q) => isListKey(q.queryKey) });
    const snapshot = qc.getQueriesData<unknown>({ predicate: (q) => isListKey(q.queryKey) || isItemKey(q.queryKey) });
    for (const [key, data] of snapshot) {
        if (isListKey(key) && Array.isArray(data)) {
            qc.setQueryData(key, patchList(String(key[1]), key, data as DriveItem[]));
        } else if (patchItem && isItemKey(key) && data && typeof data === "object") {
            qc.setQueryData(key, patchItem(data as DriveItem));
        }
    }
    return snapshot;
}

function rollback(qc: QueryClient, snapshot: Snapshot | undefined) {
    if (!snapshot) return;
    for (const [key, data] of snapshot) qc.setQueryData(key, data);
}

/**
 * Runs `fn` over every input with bounded parallelism. Throws one error
 * summarising the failures (after every input has been attempted).
 */
export async function runAll<T, R>(inputs: T[], fn: (input: T) => Promise<R>, limit = 4): Promise<R[]> {
    const results: R[] = [];
    const errors: unknown[] = [];
    let next = 0;
    const worker = async () => {
        while (next < inputs.length) {
            const i = next++;
            try {
                results[i] = await fn(inputs[i]!);
            } catch (e) {
                errors.push(e);
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, inputs.length) }, worker));
    if (errors.length > 0) {
        const first = errors[0];
        const msg = first instanceof Error ? first.message : String(first);
        throw new Error(errors.length === inputs.length ? msg : `${errors.length} of ${inputs.length} failed — ${msg}`);
    }
    return results;
}

// ── Mutations ───────────────────────────────────────────────────────

export function useCreateFolder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ parentId, name }: { parentId: string | null; name: string }) =>
            driveApi.createFolder(parentId, name, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useRenameItem() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; name: string }) => driveApi.rename(args.id, args.name, token),
        onMutate: ({ id, name }) =>
            patchCaches(
                qc,
                (_kind, _key, list) => list.map((i) => (i.id === id ? { ...i, name } : i)),
                (i) => (i.id === id ? { ...i, name } : i)
            ),
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useToggleFavorite() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ items, fav }: { items: DriveItem[]; fav: boolean }) =>
            runAll(items, (i) => (fav ? driveApi.favorite(i.id, token) : driveApi.unfavorite(i.id, token))),
        onMutate: ({ items, fav }) => {
            const ids = new Set(items.map((i) => i.id));
            return patchCaches(
                qc,
                (kind, _key, list) => {
                    const mapped = list.map((i) => (ids.has(i.id) ? { ...i, isFavorite: fav } : i));
                    if (kind !== "favorites") return mapped;
                    if (!fav) return mapped.filter((i) => !ids.has(i.id));
                    const present = new Set(mapped.map((i) => i.id));
                    return [...items.filter((i) => !present.has(i.id)).map((i) => ({ ...i, isFavorite: true })), ...mapped];
                },
                (i) => (ids.has(i.id) ? { ...i, isFavorite: fav } : i)
            );
        },
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

/** Soft delete (Move to bin). */
export function useTrashItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (ids: string[]) => runAll(ids, (id) => driveApi.trash(id, token)),
        onMutate: (ids) => {
            const set = new Set(ids);
            return patchCaches(qc, (kind, _key, list) => (kind === "trash" ? list : list.filter((i) => !set.has(i.id))));
        },
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useRestoreItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (ids: string[]) => runAll(ids, (id) => driveApi.restore(id, token)),
        onMutate: (ids) => {
            const set = new Set(ids);
            return patchCaches(qc, (kind, _key, list) => (kind === "trash" ? list.filter((i) => !set.has(i.id)) : list));
        },
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

/** Permanent delete (Delete forever). */
export function useDeleteItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (ids: string[]) => runAll(ids, (id) => driveApi.deleteItem(id, token)),
        onMutate: (ids) => {
            const set = new Set(ids);
            return patchCaches(qc, (_kind, _key, list) => list.filter((i) => !set.has(i.id)));
        },
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useEmptyTrash() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: () => driveApi.emptyTrash(token),
        onMutate: () => patchCaches(qc, (kind, _key, list) => (kind === "trash" ? [] : list)),
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export interface MoveSpec {
    item: DriveItem;
    newParentId: string | null;
}

export function useMoveItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (moves: MoveSpec[]) => runAll(moves, (m) => driveApi.move(m.item.id, m.newParentId, token)),
        onMutate: (moves) => {
            const byId = new Map(moves.map((m) => [m.item.id, m]));
            return patchCaches(qc, (kind, key, list) => {
                if (kind !== "items") {
                    return list.map((i) => {
                        const m = byId.get(i.id);
                        return m ? { ...i, parentId: m.newParentId } : i;
                    });
                }
                const listParent = (key[2] ?? null) as string | null;
                const kept = list.filter((i) => {
                    const m = byId.get(i.id);
                    return !m || m.newParentId === listParent;
                });
                const present = new Set(kept.map((i) => i.id));
                const arriving = moves
                    .filter((m) => m.newParentId === listParent && !present.has(m.item.id))
                    .map((m) => ({ ...m.item, parentId: m.newParentId }));
                return [...kept, ...arriving];
            });
        },
        onError: (_e, _v, snapshot) => rollback(qc, snapshot),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useCopyItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ ids, parentId }: { ids: string[]; parentId?: string | null }) =>
            runAll(
                ids,
                (id) => driveApi.copy(id, parentId === undefined ? undefined : parentId === null ? { toRoot: true } : { parentId }, token),
                2
            ),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

/** "New" from a template: a real copy in the caller's Drive. */
export function useCopyTemplate() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ id, parentId, name }: { id: string; parentId: string | null; name?: string }) =>
            driveApi.copyTemplate(id, { parentId, name }, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useZipItems() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ ids, name, parentId }: { ids: string[]; name: string; parentId: string | null }) =>
            driveApi.zip(ids, name, parentId, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useUnzip() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => driveApi.unzip(id, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useProtectPdf() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (a: { id: string; userPassword: string; ownerPassword: string; editLock: boolean }) =>
            driveApi.protectPdf(a.id, a.userPassword, a.ownerPassword, a.editLock, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

// ── File requests ───────────────────────────────────────────────────

export function useFileRequests(folderId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "file-requests", folderId, token],
        queryFn: () => (folderId ? driveApi.listFileRequests(folderId, token) : Promise.resolve([])),
        enabled: Boolean(folderId) && Boolean(token)
    });
}

export function useCreateFileRequest() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (a: { folderId: string; title?: string; instructions?: string; expiresAt?: string | null }) =>
            driveApi.createFileRequest(a.folderId, { title: a.title, instructions: a.instructions, expiresAt: a.expiresAt }, token),
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: ["drive", "file-requests"] });
            void qc.invalidateQueries({ queryKey: ["drive", "activity"] });
        }
    });
}

export function useUpdateFileRequest() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (a: { id: string; action: "close" | "reopen" | "delete" }) =>
            a.action === "delete"
                ? driveApi.deleteFileRequest(a.id, token)
                : driveApi.setFileRequestClosed(a.id, a.action === "close", token).then(() => undefined),
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: ["drive", "file-requests"] });
            void qc.invalidateQueries({ queryKey: ["drive", "activity"] });
        }
    });
}

/** "Uploaded by" for a file that came in through a file request (null otherwise). */
export function useUploadedBy(fileId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive", "uploaded-by", fileId, token],
        queryFn: () => (fileId ? driveApi.uploadedBy(fileId, token) : Promise.resolve(null)),
        enabled: Boolean(fileId) && Boolean(token),
        staleTime: 5 * 60_000,
        retry: false
    });
}

// ── Sharing (used by ShareDialog — keep signatures stable) ──────────

export function useCreateShare() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: {
            id: string;
            permission?: "view" | "edit";
            audience?: "anonymous" | "domain" | "email";
            emails?: string[];
            expiresAt?: string | null;
            password?: string | null;
        }) => driveApi.createShare(args.id, args, token)
    });
}

export function useRevokeShare() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (shareId: string) => driveApi.revokeShare(shareId, token),
        onSuccess: () => { void qc.invalidateQueries({ queryKey: ["drive", "shares"] }); }
    });
}

// ── Versions ────────────────────────────────────────────────────────

export function useUploadNewVersion() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ id, file }: { id: string; file: File }) => driveApi.uploadNewVersion(id, file, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

/** "Delete old versions" — keeps only the current one. Refreshes quota, storage and activity. */
export function usePruneVersions() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => driveApi.pruneVersions(id, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}

export function useRestoreVersion() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ id, versionId }: { id: string; versionId: string }) => driveApi.restoreVersion(id, versionId, token),
        onSettled: () => { void invalidateDrive(qc); }
    });
}
